import { EllipsisVerticalIcon } from "@heroicons/react/16/solid";
import { get } from "lodash-es";
import posthog from "posthog-js";
import React, { FC, useCallback, useRef, useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { MenuItem, MenuList, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import { GridColDef, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { GridRowSelectionModel } from "@mui/x-data-grid/models/gridRowSelectionModel";

import ActionToolbar from "../../components/ActionToolbar";
import DataGridPro from "../../components/DataGridPro";
import DataGridProToolbar from "../../components/DataGridProToolbar";
import DeleteActionDialog, { Entry } from "../../components/DeleteActionDialog";
import Header from "../../components/Header";
import { LoadingError } from "../../components/LoadingError";
import Page from "../../components/Page";
import Quota from "../../components/Quota";
import SvgIcon from "../../components/styled/SvgIcon";
import { useDialog } from "../../hooks/use-dialog";
import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import { usePopover } from "../../hooks/use-popover";
import useQuota from "../../hooks/use-quota";
import { useSavedColumnWidths } from "../../hooks/use-saved-column-widths";
import { setBannerMessage } from "../../slices/pricing_info";
import { useDispatch, useSelector } from "../../store";
import {
  useDeleteServiceAccountMutation,
  useListServiceAccountsQuery,
} from "../../store/pomeriumApi";
import {
  createdAt,
  dateTimeExpiresAt,
  updatedAt,
} from "../../utils/data-grid-col-defs";
import AddServiceAccount from "./AddServiceAccount";

const defaultColumns = JSON.stringify({
  id: false,
  originatorId: false,
});

const AccountList: FC = () => {
  const actionsPopover = usePopover();
  const dispatch = useDispatch();
  const anchorRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigateWithSearch();
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const [serviceAccountId, setServiceAccountId] = useState<string>("");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<string>(
    localStorage.getItem("serviceAccountColumns") || defaultColumns,
  );
  const { mutateColumnWidths, onColumnWidthChange } =
    useSavedColumnWidths("accounts");
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);
  const [showDelete, setShowDelete] = useState(false);
  const addDialog = useDialog<never>();
  const { count: serviceAccountCount, limit: serviceAccountLimit } =
    useQuota("serviceAccounts");

  const { byId: orgById } = useSelector((state) => state.organizations);
  const readOnly = get(orgById, organizationId + ".readOnly", false) as boolean;

  const [deleteAccount] = useDeleteServiceAccountMutation();

  async function handleMultiDelete() {
    await Promise.all(
      selected.map((id) =>
        deleteAccount({
          organizationId,
          serviceAccountId: id.toString(),
          clusterId,
        }),
      ),
    );
    posthog.capture("service_accounts_deleted", {
      deleted: selected.toString(),
    });
  }

  const deleteOne = () => {
    setSelected([serviceAccountId]);
    setShowDelete(true);
    actionsPopover.handleClose();
  };

  const onAdd = (evt: React.MouseEvent): void => {
    evt.preventDefault();
    if (readOnly) {
      dispatch(
        setBannerMessage(
          "To add another service account, renew your subscription.",
        ),
      );
    } else if (serviceAccountCount >= serviceAccountLimit) {
      dispatch(
        setBannerMessage(
          "To add another service account, upgrade your account.",
        ),
      );
    } else {
      addDialog.handleOpen();
    }
  };

  const {
    data: serviceAccounts,
    error,
    isLoading,
    isFetching,
  } = useListServiceAccountsQuery(
    {
      organizationId,
      clusterId,
    },
    { skip: !organizationId || !clusterId },
  );

  let columns: GridColDef[] = [
    {
      display: "flex",
      headerName: "Originator ID",
      field: "originatorId",
      flex: 1,
    },
    {
      display: "flex",
      headerName: "User ID",
      field: "userId",
      flex: 1,
    },
    {
      display: "flex",
      headerName: "Description",
      field: "description",
      flex: 1,
    },
    dateTimeExpiresAt,
    createdAt,
    updatedAt,
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: function action(params) {
        return (
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              onClick={(e) => {
                anchorRef.current = e.currentTarget;
                setServiceAccountId(params.row.id);
                actionsPopover.handleOpen();
              }}
              sx={{ width: 4 }}
            >
              <SvgIcon color="disabled">
                <EllipsisVerticalIcon />
              </SvgIcon>
            </IconButton>
          </Grid>
        );
      },
    },
  ];
  columns = mutateColumnWidths(columns);

  const CustomToolbar = useCallback(
    () => (
      <Stack direction="row" justifyContent="space-between">
        <DataGridProToolbar>
          <ActionToolbar
            selectedIds={selected}
            onDelete={() => setShowDelete(true)}
          />
        </DataGridProToolbar>
        <GridToolbarQuickFilter variant="outlined" size="small" sx={{ m: 1 }} />
      </Stack>
    ),
    [JSON.stringify(selected), setShowDelete],
  );

  if (error) {
    return (
      <Page title="Service Accounts">
        <Header title="Service Accounts" breadcrumbs={true} />
        <LoadingError />
      </Page>
    );
  }

  const actions = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Quota quota="serviceAccounts" label="accounts" />
      <Button
        variant="contained"
        color="primary"
        onClick={onAdd}
        startIcon={<AddCircleOutlineIcon />}
      >
        New Service Account
      </Button>
    </Stack>
  );

  return (
    <Page title="Service Accounts">
      <Header title="Service Accounts" breadcrumbs={true} actions={actions} />
      <DeleteActionDialog
        name="service accounts"
        open={showDelete}
        entries={
          serviceAccounts?.filter((r) => selected.includes(r.id)) ||
          ([] as Entry[])
        }
        onClose={() => setShowDelete(false)}
        onDelete={handleMultiDelete}
      />
      <AddServiceAccount dialog={addDialog} />
      <Popover
        anchorEl={anchorRef.current}
        disableScrollLock
        onClose={actionsPopover.handleClose}
        open={actionsPopover.open}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
            },
          },
        }}
      >
        <MenuList>
          <MenuItem
            onClick={() =>
              navigate(
                "/app/management/service-accounts/" +
                  serviceAccountId +
                  "/view",
              )
            }
          >
            View
          </MenuItem>
          {!readOnly && <MenuItem onClick={deleteOne}>Delete</MenuItem>}
        </MenuList>
      </Popover>
      <Container component={Paper} maxWidth={false} disableGutters={true}>
        <DataGridPro
          loading={isLoading || isFetching}
          rows={clusterId ? serviceAccounts || [] : []}
          columns={columns}
          columnVisibilityModel={JSON.parse(columnVisibilityModel)}
          onColumnVisibilityModelChange={(newModel) => {
            localStorage.setItem(
              "serviceAccountColumns",
              JSON.stringify(newModel),
            );
            setColumnVisibilityModel(JSON.stringify(newModel));
          }}
          onColumnWidthChange={onColumnWidthChange}
          slots={{
            toolbar: CustomToolbar,
          }}
          onRowSelectionModelChange={(model) => setSelected(model)}
          isRowSelectable={() => !readOnly}
        />
      </Container>
    </Page>
  );
};
export default AccountList;
